import { Stack } from "@mui/material"

import type { FC } from "react"
import type { InstalledOnInfo, InstalledOnLoading } from "types/device.types"

import useSpan from "helpers/hooks/spans/useSpan"
import EntityLink from "widgets/common/EntityLink"
import SkeletonBox from "widgets/common/SkeletonBox"
import InstalledOnParentLink from "widgets/device/InstalledOnParentLink"

interface InstalledOnCellProps {
  installedOn: InstalledOnInfo
  loading?: InstalledOnLoading
}

/**
 * Where a device sits, in two lines: the asset it is clamped to over the line
 * that asset belongs to. One glyph, on the first line, because the second is
 * always a power line and its type says nothing new.
 */
const InstalledOnCell: FC<InstalledOnCellProps> = ({ installedOn, loading }) => {
  const { type, spanId, powerline } = installedOn

  // A cell per rendered row, so this asks for the spans actually on screen
  // rather than every span of their lines, and two devices on one span share
  // the cached answer.
  const { span, isLoading: spanLoading } = useSpan({ id: spanId })

  // The line is the second half of both answers, and a span's own name falls
  // back to its position on it, so neither type can render until it is in.
  const showSkeleton =
    (type === "tower" && loading?.towers) || loading?.powerlines || spanLoading

  if (showSkeleton) {
    return <SkeletonBox height={20} width={140} />
  }

  return (
    <Stack>
      <InstalledOnParentLink installedOn={installedOn} span={span} />
      {/* A tower does not have to belong to a line, and then the reference is
          one line and that is the whole answer. */}
      {powerline && (
        <EntityLink
          entity="powerline"
          id={powerline.id}
          name={powerline.name}
          noGlyph
          secondary
          maxLines={1}
        />
      )}
    </Stack>
  )
}

export default InstalledOnCell
